import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import {
  trackWhatsAppClick,
  trackEventCustom,
} from "../../utils/analytics";

/* =========================================================
   PAGE CONFIG
========================================================= */

const SITE_URL = "https://hexxatech.co.in";
const PAGE_URL = `${SITE_URL}/services/custom-software-development`;

const WHATSAPP_URL =
  "https://wa.me/919226591664?text=Hello%20HexxaTech%2C%20I%20would%20like%20to%20discuss%20a%20custom%20software%20development%20project.";

/* =========================================================
   KEY FEATURES
========================================================= */

const features = [
  {
    icon: "⌘",
    title: "Business Workflow Automation",
    text: "Convert repetitive manual processes into structured digital workflows that are easier to manage, monitor and scale.",
  },
  {
    icon: "▦",
    title: "Custom ERP & Business Systems",
    text: "Build centralized systems for purchasing, sales, inventory, quotations, billing, operations, approvals and reporting.",
  },
  {
    icon: "◎",
    title: "Custom CRM Solutions",
    text: "Manage leads, customers, follow-ups, appointments, sales activities and customer information from one platform.",
  },
  {
    icon: "◇",
    title: "Database-Driven Applications",
    text: "Develop secure applications around your business data, relationships, transactions and reporting requirements.",
  },
  {
    icon: "⚙",
    title: "API & System Integration",
    text: "Connect your custom software with existing applications, APIs, databases and third-party business systems.",
  },
  {
    icon: "▯",
    title: "Web & Mobile Applications",
    text: "Develop responsive web applications and business-focused mobile applications for teams working in the field or office.",
  },
];

/* =========================================================
   BUSINESS PROBLEMS
========================================================= */

const problems = [
  {
    number: "01",
    title: "Too Much Manual Work",
    text: "Reduce spreadsheets, repetitive data entry, paper-based processes and disconnected workflows.",
  },
  {
    number: "02",
    title: "Scattered Business Data",
    text: "Bring customer, transaction, inventory, operational and reporting data into a centralized system.",
  },
  {
    number: "03",
    title: "Generic Software Limitations",
    text: "Build functionality around your actual business rules instead of changing your processes to fit generic software.",
  },
  {
    number: "04",
    title: "Limited Business Visibility",
    text: "Create dashboards, reports and centralized information so management can make faster operational decisions.",
  },
];

/* =========================================================
   DEVELOPMENT PROCESS
========================================================= */

const processSteps = [
  {
    number: "01",
    title: "Business Discovery",
    text: "We understand your business model, departments, users, workflows, challenges, approvals and reporting requirements.",
  },
  {
    number: "02",
    title: "Requirement Analysis",
    text: "We convert business requirements into functional modules, user roles, workflows, database requirements and system specifications.",
  },
  {
    number: "03",
    title: "Architecture & Planning",
    text: "We define application architecture, database structure, APIs, integrations, security requirements and technology approach.",
  },
  {
    number: "04",
    title: "UI/UX & Development",
    text: "We design practical interfaces and develop the application module-by-module around the approved business workflow.",
  },
  {
    number: "05",
    title: "Testing & Validation",
    text: "The system is tested against business scenarios, workflows, permissions, data handling and functional requirements.",
  },
  {
    number: "06",
    title: "Deployment & Support",
    text: "After deployment, we provide technical support, maintenance, improvements and future development as your business evolves.",
  },
];

/* =========================================================
   USE CASES
========================================================= */

const useCases = [
  "Custom ERP Software",
  "CRM & Lead Management",
  "Inventory Management",
  "Healthcare Management",
  "Manufacturing Software",
  "Process Control Systems",
  "License Management",
  "Business Dashboards",
  "Workflow Automation",
  "Field Operations Software",
  "Customer Portals",
  "Internal Business Applications",
];

/* =========================================================
   TECHNOLOGY CAPABILITIES
========================================================= */

const capabilities = [
  "Web Applications",
  "Business Management Systems",
  "Database Applications",
  "REST APIs",
  "Dashboard & Reporting",
  "Role-Based Access",
  "Workflow Automation",
  "Third-Party Integrations",
  "Mobile Applications",
  "Offline Data Synchronization",
];

/* =========================================================
   FAQ
========================================================= */

const faqs = [
  {
    question: "What is custom software development?",
    answer:
      "Custom software development is the process of designing and developing software specifically around the workflows, business rules, users, data and operational requirements of an organization instead of relying entirely on generic software.",
  },
  {
    question: "Why choose custom software instead of ready-made software?",
    answer:
      "Custom software allows businesses to implement their own workflows, approvals, reporting requirements, integrations and business rules. It can also evolve as the organization grows instead of being restricted by the features of a generic platform.",
  },
  {
    question: "Can HexxaTech develop software for a specific industry?",
    answer:
      "Yes. HexxaTech develops custom software for different business requirements including automotive, healthcare, manufacturing, business operations, inventory management, software management and other specialized workflows.",
  },
  {
    question: "Can custom software integrate with existing systems?",
    answer:
      "Yes. Custom applications can be designed to integrate with existing databases, APIs, internal systems and third-party services depending on their technical capabilities and integration requirements.",
  },
  {
    question: "Does HexxaTech provide maintenance after development?",
    answer:
      "Yes. HexxaTech can provide ongoing technical support, maintenance, improvements, troubleshooting and further development after the initial software deployment.",
  },
];

const serviceNavigation = [
  { title: "Custom Software Development", to: "/services/custom-software-development" },
  { title: "ERP & Business Management", to: "/services/erp-business-management" },
  { title: "CRM & Lead Management", to: "/services/crm-lead-management" },
  { title: "Healthcare Software", to: "/services/healthcare-software" },
  { title: "Inventory Management", to: "/services/inventory-management" },
  { title: "Industrial Process Software", to: "/services/industrial-process-software" },
  { title: "License Management", to: "/services/license-management" },
  { title: "Mobile App Development", to: "/services/mobile-app-development" },
];

/* =========================================================
   COMPONENT
========================================================= */

export default function CustomSoftwareDevelopment() {
  useEffect(() => {
    /* =====================================================
       BASIC SEO
    ===================================================== */

    document.title =
      "Custom Software Development Company in Pune | HexxaTech Softwares";

    const description =
      "HexxaTech Softwares is a custom software development company in Pune building ERP, CRM, healthcare, inventory, industrial and business automation software around real business workflows.";

    const setMeta = (name, content) => {
      let element = document.querySelector(`meta[name="${name}"]`);

      if (!element) {
        element = document.createElement("meta");
        element.setAttribute("name", name);
        document.head.appendChild(element);
      }

      element.setAttribute("content", content);
    };

    setMeta("description", description);

    /* =====================================================
       CANONICAL
    ===================================================== */

    let canonical = document.querySelector('link[rel="canonical"]');

    if (!canonical) {
      canonical = document.createElement("link");
      canonical.setAttribute("rel", "canonical");
      document.head.appendChild(canonical);
    }

    canonical.setAttribute("href", PAGE_URL);

    /* =====================================================
       OPEN GRAPH
    ===================================================== */

    const setPropertyMeta = (property, content) => {
      let element = document.querySelector(
        `meta[property="${property}"]`
      );

      if (!element) {
        element = document.createElement("meta");
        element.setAttribute("property", property);
        document.head.appendChild(element);
      }

      element.setAttribute("content", content);
    };

    setPropertyMeta(
      "og:title",
      "Custom Software Development Company in Pune | HexxaTech"
    );

    setPropertyMeta(
      "og:description",
      "Custom software development for ERP, CRM, healthcare, inventory, industrial workflows and business automation."
    );

    setPropertyMeta("og:type", "website");
    setPropertyMeta("og:url", PAGE_URL);
    setPropertyMeta("og:site_name", "HexxaTech Softwares");

    /* =====================================================
       TWITTER META
    ===================================================== */

    setMeta("twitter:card", "summary_large_image");

    setPropertyMeta(
      "twitter:title",
      "Custom Software Development Company in Pune | HexxaTech"
    );

    setPropertyMeta(
      "twitter:description",
      "Custom software solutions designed around real business workflows."
    );

    /* =====================================================
       STRUCTURED DATA
    ===================================================== */

    const existingSchema = document.getElementById(
      "hexxatech-custom-software-schema"
    );

    if (existingSchema) {
      existingSchema.remove();
    }

    const script = document.createElement("script");

    script.id = "hexxatech-custom-software-schema";
    script.type = "application/ld+json";

    script.text = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "Service",
      name: "Custom Software Development",
      serviceType: "Custom Software Development",
      description:
        "Custom software development services for ERP, CRM, healthcare, inventory, industrial process management and business automation.",
      url: PAGE_URL,
      provider: {
        "@type": "Organization",
        name: "HexxaTech Softwares",
        url: SITE_URL,
      },
      areaServed: [
        {
          "@type": "City",
          name: "Pune",
        },
        {
          "@type": "Country",
          name: "India",
        },
      ],
      audience: {
        "@type": "BusinessAudience",
        audienceType: "Businesses and Organizations",
      },
    });

    document.head.appendChild(script);

    /* =====================================================
       FAQ STRUCTURED DATA
    ===================================================== */

    const existingFaqSchema = document.getElementById(
      "hexxatech-custom-software-faq-schema"
    );

    if (existingFaqSchema) {
      existingFaqSchema.remove();
    }

    const faqScript = document.createElement("script");

    faqScript.id = "hexxatech-custom-software-faq-schema";
    faqScript.type = "application/ld+json";

    faqScript.text = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: faqs.map((faq) => ({
        "@type": "Question",
        name: faq.question,
        acceptedAnswer: {
          "@type": "Answer",
          text: faq.answer,
        },
      })),
    });

    document.head.appendChild(faqScript);

    /* =====================================================
       CLEANUP
    ===================================================== */

    return () => {
      const schema = document.getElementById(
        "hexxatech-custom-software-schema"
      );

      const faqSchema = document.getElementById(
        "hexxatech-custom-software-faq-schema"
      );

      if (schema) schema.remove();
      if (faqSchema) faqSchema.remove();
    };
  }, []);

  const currentIndex = serviceNavigation.findIndex(
    (service) => service.to === PAGE_URL
  );
  const previousService =
    serviceNavigation[(currentIndex - 1 + serviceNavigation.length) % serviceNavigation.length];
  const nextService =
    serviceNavigation[(currentIndex + 1) % serviceNavigation.length];

  return (
    <div className="hexxa-service-page">
      <style>{`

        * {
          box-sizing: border-box;
        }

        html {
          scroll-behavior: smooth;
        }

        .hexxa-service-page {
          overflow: hidden;
          background: #ffffff;
          color: #0f172a;
          font-family: Inter, -apple-system, BlinkMacSystemFont,
            "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
        }

        .hexxa-service-container {
          width: min(1180px, calc(100% - 40px));
          margin: 0 auto;
        }

        /* =================================================
           HERO
        ================================================= */

        .hexxa-service-hero {
          position: relative;
          overflow: hidden;
          padding: 105px 0 90px;
          color: #ffffff;
          background:
            radial-gradient(
              circle at 82% 18%,
              rgba(59, 130, 246, .20),
              transparent 30%
            ),
            linear-gradient(
              135deg,
              #294a7d 0%,
              #355b91 55%,
              #294a7d 100%
            );
        }

        .hexxa-service-hero::before {
          content: "";
          position: absolute;
          width: 500px;
          height: 500px;
          border-radius: 50%;
          border: 1px solid rgba(255,255,255,.06);
          right: -200px;
          top: -250px;
        }

        .hexxa-service-hero-grid {
          position: relative;
          z-index: 1;
          display: grid;
          grid-template-columns: 1.1fr .9fr;
          gap: 70px;
          align-items: center;
        }

        .hexxa-breadcrumb {
          display: flex;
          flex-wrap: wrap;
          align-items: center;
          gap: 7px;
          margin-bottom: 25px;
          color: #bfdbfe;
          font-size: 12px;
        }

        .hexxa-breadcrumb a {
          color: #dbeafe;
          text-decoration: none;
        }

        .hexxa-breadcrumb a:hover {
          text-decoration: underline;
        }

        .hexxa-service-eyebrow {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          padding: 7px 12px;
          border-radius: 999px;
          background: rgba(255,255,255,.10);
          border: 1px solid rgba(255,255,255,.16);
          color: #dbeafe;
          font-size: 11px;
          font-weight: 800;
          letter-spacing: 1.2px;
          text-transform: uppercase;
          margin-bottom: 20px;
        }

        .hexxa-service-eyebrow-dot {
          width: 6px;
          height: 6px;
          border-radius: 50%;
          background: #60a5fa;
        }

        .hexxa-service-hero h1 {
          max-width: 750px;
          margin: 0;
          font-size: clamp(42px, 5vw, 64px);
          line-height: 1.03;
          letter-spacing: -2.8px;
          font-weight: 800;
        }

        .hexxa-service-hero h1 span {
          color: #60a5fa;
        }

        .hexxa-service-hero-text {
          max-width: 700px;
          margin: 25px 0 30px;
          color: #dbeafe;
          font-size: 17px;
          line-height: 1.8;
        }

        .hexxa-service-buttons {
          display: flex;
          gap: 12px;
          flex-wrap: wrap;
        }

        .hexxa-service-btn {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          min-height: 48px;
          padding: 0 20px;
          border-radius: 10px;
          text-decoration: none;
          font-size: 14px;
          font-weight: 700;
          transition: .25s ease;
        }

        .hexxa-service-btn-primary {
          background: #2563eb;
          color: #ffffff;
          box-shadow: 0 12px 25px rgba(15,23,42,.20);
        }

        .hexxa-service-btn-primary:hover {
          transform: translateY(-2px);
          background: #1d4ed8;
        }

        .hexxa-service-btn-light {
          background: rgba(255,255,255,.08);
          color: #ffffff;
          border: 1px solid rgba(255,255,255,.22);
        }

        .hexxa-service-btn-light:hover {
          background: rgba(255,255,255,.15);
        }

        /* =================================================
           HERO VISUAL
        ================================================= */

        .hexxa-service-visual {
          position: relative;
          min-height: 370px;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .hexxa-service-card {
          width: min(470px, 100%);
          padding: 28px;
          border-radius: 24px;
          background: rgba(255,255,255,.09);
          border: 1px solid rgba(255,255,255,.18);
          backdrop-filter: blur(12px);
          box-shadow: 0 30px 70px rgba(15,23,42,.25);
          transform: rotate(-2deg);
        }

        .hexxa-service-card-top {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding-bottom: 22px;
          border-bottom: 1px solid rgba(255,255,255,.12);
        }

        .hexxa-service-card-title {
          font-size: 13px;
          color: #dbeafe;
          font-weight: 700;
        }

        .hexxa-service-card-status {
          padding: 5px 9px;
          border-radius: 999px;
          background: rgba(96,165,250,.15);
          color: #bfdbfe;
          font-size: 9px;
          font-weight: 800;
          text-transform: uppercase;
          letter-spacing: .8px;
        }

        .hexxa-service-modules {
          display: grid;
          grid-template-columns: repeat(2, 1fr);
          gap: 12px;
          margin-top: 22px;
        }

        .hexxa-service-module {
          padding: 18px;
          min-height: 90px;
          border-radius: 14px;
          background: rgba(255,255,255,.09);
          border: 1px solid rgba(255,255,255,.08);
        }

        .hexxa-service-module-icon {
          width: 30px;
          height: 30px;
          display: flex;
          align-items: center;
          justify-content: center;
          margin-bottom: 12px;
          border-radius: 8px;
          background: rgba(96,165,250,.15);
          color: #93c5fd;
          font-size: 12px;
          font-weight: 800;
        }

        .hexxa-service-module strong {
          display: block;
          color: #ffffff;
          font-size: 12px;
        }

        .hexxa-service-module small {
          display: block;
          margin-top: 4px;
          color: #bfdbfe;
          font-size: 9px;
        }

        /* =================================================
           COMMON
        ================================================= */

        .hexxa-service-section {
          padding: 90px 0;
        }

        .hexxa-service-section-soft {
          background: #f8fafc;
        }

        .hexxa-service-label {
          color: #2563eb;
          font-size: 11px;
          font-weight: 800;
          letter-spacing: 1.5px;
          text-transform: uppercase;
        }

        .hexxa-service-title {
          max-width: 800px;
          margin: 12px 0 0;
          color: #0f172a;
          font-size: clamp(32px, 4vw, 48px);
          line-height: 1.1;
          letter-spacing: -1.8px;
        }

        .hexxa-service-description {
          max-width: 760px;
          margin: 18px 0 0;
          color: #64748b;
          font-size: 16px;
          line-height: 1.8;
        }

        /* =================================================
           INTRO
        ================================================= */

        .hexxa-service-intro-grid {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 70px;
          align-items: center;
        }

        .hexxa-service-intro-text p {
          color: #64748b;
          font-size: 15px;
          line-height: 1.9;
        }

        .hexxa-service-intro-text p:first-child {
          margin-top: 22px;
        }

        .hexxa-service-intro-box {
          padding: 35px;
          border-radius: 22px;
          background: #ffffff;
          border: 1px solid #e5e7eb;
          box-shadow: 0 20px 50px rgba(15,23,42,.06);
        }

        .hexxa-service-intro-box h3 {
          margin: 0 0 22px;
          font-size: 21px;
        }

        .hexxa-intro-list {
          display: grid;
          gap: 12px;
        }

        .hexxa-intro-item {
          display: flex;
          align-items: center;
          gap: 10px;
          padding: 13px;
          border-radius: 10px;
          background: #f8fafc;
          color: #475569;
          font-size: 13px;
        }

        .hexxa-intro-check {
          display: flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
          width: 23px;
          height: 23px;
          border-radius: 50%;
          background: #eff6ff;
          color: #2563eb;
          font-size: 11px;
          font-weight: 800;
        }

        /* =================================================
           FEATURES
        ================================================= */

        .hexxa-service-header-center {
          max-width: 800px;
          margin: 0 auto 45px;
          text-align: center;
        }

        .hexxa-service-header-center .hexxa-service-title,
        .hexxa-service-header-center .hexxa-service-description {
          margin-left: auto;
          margin-right: auto;
        }

        .hexxa-feature-grid {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 18px;
        }

        .hexxa-feature {
          min-height: 235px;
          padding: 27px;
          border-radius: 17px;
          background: #ffffff;
          border: 1px solid #e5e7eb;
          transition: .3s ease;
        }

        .hexxa-feature:hover {
          transform: translateY(-5px);
          border-color: #bfdbfe;
          box-shadow: 0 18px 40px rgba(15,23,42,.07);
        }

        .hexxa-feature-icon {
          width: 43px;
          height: 43px;
          display: flex;
          align-items: center;
          justify-content: center;
          margin-bottom: 20px;
          border-radius: 11px;
          background: #eff6ff;
          color: #2563eb;
          font-size: 18px;
          font-weight: 800;
        }

        .hexxa-feature h3 {
          margin: 0 0 10px;
          font-size: 17px;
        }

        .hexxa-feature p {
          margin: 0;
          color: #64748b;
          font-size: 13px;
          line-height: 1.75;
        }

        /* =================================================
           PROBLEMS
        ================================================= */

        .hexxa-problem-grid {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 18px;
          margin-top: 45px;
        }

        .hexxa-problem {
          padding: 25px;
          border-radius: 16px;
          background: #ffffff;
          border: 1px solid #e5e7eb;
        }

        .hexxa-problem-number {
          color: #2563eb;
          font-size: 11px;
          font-weight: 800;
          letter-spacing: 1px;
        }

        .hexxa-problem h3 {
          margin: 9px 0;
          font-size: 17px;
        }

        .hexxa-problem p {
          margin: 0;
          color: #64748b;
          font-size: 13px;
          line-height: 1.75;
        }

        /* =================================================
           PROCESS
        ================================================= */

        .hexxa-process-grid {
          display: grid;
          grid-template-columns: .85fr 1.15fr;
          gap: 70px;
          align-items: start;
        }

        .hexxa-process-list {
          display: grid;
          gap: 10px;
          margin-top: 35px;
        }

        .hexxa-process-item {
          padding: 20px;
          border-radius: 14px;
          background: #ffffff;
          border: 1px solid #e5e7eb;
          transition: .25s ease;
        }

        .hexxa-process-item:hover {
          transform: translateX(4px);
          border-color: #bfdbfe;
        }

        .hexxa-process-number {
          color: #2563eb;
          font-size: 11px;
          font-weight: 800;
          letter-spacing: 1px;
        }

        .hexxa-process-item h3 {
          margin: 7px 0;
          font-size: 15px;
        }

        .hexxa-process-item p {
          margin: 0;
          color: #64748b;
          font-size: 12px;
          line-height: 1.7;
        }

        .hexxa-process-visual {
          padding: 35px;
          border-radius: 24px;
          background: linear-gradient(145deg, #eff6ff, #ffffff);
          border: 1px solid #dbeafe;
        }

        .hexxa-process-flow {
          display: grid;
          gap: 10px;
          margin-top: 25px;
        }

        .hexxa-process-flow-row {
          display: flex;
          align-items: center;
          gap: 12px;
          padding: 14px;
          border-radius: 12px;
          background: #ffffff;
          border: 1px solid #e5e7eb;
        }

        .hexxa-process-flow-icon {
          width: 31px;
          height: 31px;
          display: flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
          border-radius: 8px;
          background: #eff6ff;
          color: #2563eb;
          font-size: 10px;
          font-weight: 800;
        }

        .hexxa-process-flow-row strong {
          font-size: 12px;
        }

        /* =================================================
           USE CASES
        ================================================= */

        .hexxa-usecase-grid {
          display: flex;
          flex-wrap: wrap;
          gap: 10px;
          margin-top: 35px;
        }

        .hexxa-usecase {
          padding: 12px 15px;
          border-radius: 10px;
          background: #ffffff;
          border: 1px solid #e5e7eb;
          color: #475569;
          font-size: 12px;
          font-weight: 600;
        }

        /* =================================================
           CAPABILITIES
        ================================================= */

        .hexxa-capability-grid {
          display: grid;
          grid-template-columns: repeat(5, 1fr);
          gap: 12px;
          margin-top: 35px;
        }

        .hexxa-capability {
          padding: 18px 12px;
          text-align: center;
          border-radius: 12px;
          background: #f8fafc;
          border: 1px solid #e5e7eb;
          color: #475569;
          font-size: 12px;
          font-weight: 600;
        }

        /* =================================================
           WHY HEXXATECH
        ================================================= */

        .hexxa-why-grid {
          display: grid;
          grid-template-columns: repeat(4, 1fr);
          gap: 15px;
          margin-top: 40px;
        }

        .hexxa-why {
          padding: 25px;
          border-radius: 16px;
          border: 1px solid #e5e7eb;
          background: #ffffff;
        }

        .hexxa-why strong {
          display: block;
          margin-bottom: 9px;
          font-size: 15px;
        }

        .hexxa-why span {
          color: #64748b;
          font-size: 12px;
          line-height: 1.7;
        }

        /* =================================================
           CASE STUDY
        ================================================= */

        .hexxa-related-box {
          display: grid;
          grid-template-columns: 1fr auto;
          gap: 30px;
          align-items: center;
          padding: 35px;
          border-radius: 20px;
          background: #ffffff;
          border: 1px solid #e5e7eb;
          box-shadow: 0 15px 40px rgba(15,23,42,.05);
        }

        .hexxa-related-box h3 {
          margin: 0 0 8px;
          font-size: 21px;
        }

        .hexxa-related-box p {
          margin: 0;
          color: #64748b;
          font-size: 13px;
          line-height: 1.7;
          max-width: 700px;
        }

        /* =================================================
           FAQ
        ================================================= */

        .hexxa-faq-list {
          display: grid;
          gap: 12px;
          max-width: 900px;
          margin: 40px auto 0;
        }

        .hexxa-faq {
          padding: 22px 24px;
          border-radius: 14px;
          background: #ffffff;
          border: 1px solid #e5e7eb;
        }

        .hexxa-faq h3 {
          margin: 0 0 9px;
          font-size: 15px;
        }

        .hexxa-faq p {
          margin: 0;
          color: #64748b;
          font-size: 13px;
          line-height: 1.75;
        }

        /* =================================================
           CTA
        ================================================= */

        .hexxa-service-cta {
          padding: 85px 0;
        }

        .hexxa-service-cta-box {
          position: relative;
          overflow: hidden;
          padding: 70px 30px;
          text-align: center;
          border-radius: 24px;
          color: #ffffff;
          background:
            radial-gradient(
              circle at 85% 15%,
              rgba(59,130,246,.30),
              transparent 30%
            ),
            linear-gradient(
              135deg,
              #0f172a,
              #172554
            );
        }

        .hexxa-service-cta-box::before {
          content: "";
          position: absolute;
          width: 320px;
          height: 320px;
          border-radius: 50%;
          border: 1px solid rgba(255,255,255,.08);
          top: -180px;
          left: -80px;
        }

        .hexxa-service-cta-box > * {
          position: relative;
          z-index: 1;
        }

        .hexxa-service-cta-label {
          display: inline-block;
          padding: 7px 12px;
          border-radius: 999px;
          background: rgba(255,255,255,.08);
          border: 1px solid rgba(255,255,255,.10);
          color: #bfdbfe;
          font-size: 10px;
          font-weight: 800;
          letter-spacing: 1px;
          text-transform: uppercase;
        }

        .hexxa-service-cta h2 {
          max-width: 750px;
          margin: 20px auto 12px;
          font-size: clamp(34px, 4vw, 50px);
          line-height: 1.1;
          letter-spacing: -1.5px;
        }

        .hexxa-service-cta p {
          max-width: 680px;
          margin: 0 auto 28px;
          color: #cbd5e1;
          font-size: 15px;
          line-height: 1.8;
        }

        .hexxa-service-cta .hexxa-service-btn-primary {
          background: #ffffff;
          color: #0f172a;
        }

        .hexxa-service-cta .hexxa-service-btn-primary:hover {
          background: #eff6ff;
        }

        /* =================================================
           RESPONSIVE
        ================================================= */

        @media (max-width: 1000px) {

          .hexxa-service-hero-grid,
          .hexxa-service-intro-grid,
          .hexxa-process-grid {
            grid-template-columns: 1fr;
            gap: 45px;
          }

          .hexxa-feature-grid {
            grid-template-columns: repeat(2, 1fr);
          }

          .hexxa-capability-grid {
            grid-template-columns: repeat(3, 1fr);
          }

          .hexxa-why-grid {
            grid-template-columns: repeat(2, 1fr);
          }

          .hexxa-service-visual {
            max-width: 600px;
          }
        }

        @media (max-width: 700px) {

          .hexxa-service-container {
            width: min(100% - 28px, 1180px);
          }

          .hexxa-service-hero {
            padding: 70px 0 65px;
          }

          .hexxa-service-hero h1 {
            font-size: 42px;
            letter-spacing: -1.8px;
          }

          .hexxa-service-hero-text {
            font-size: 15px;
          }

          .hexxa-service-card {
            transform: none;
          }

          .hexxa-service-section {
            padding: 65px 0;
          }

          .hexxa-feature-grid,
          .hexxa-problem-grid,
          .hexxa-why-grid {
            grid-template-columns: 1fr;
          }

          .hexxa-capability-grid {
            grid-template-columns: repeat(2, 1fr);
          }

          .hexxa-related-box {
            grid-template-columns: 1fr;
          }

          .hexxa-service-cta {
            padding: 65px 0;
          }

          .hexxa-service-cta-box {
            padding: 50px 20px;
            border-radius: 18px;
          }

          .hexxa-service-modules {
            grid-template-columns: 1fr;
          }
        }

      `}</style>

      {/* =====================================================
          HERO
      ===================================================== */}

      <section className="hexxa-service-hero">
        <div className="hexxa-service-container hexxa-service-hero-grid">

          <div>

            <div className="hexxa-breadcrumb">
              <Link to="/">Home</Link>
              <span>›</span>
              <Link to="/services">Services</Link>
              <span>›</span>
              <span>Custom Software Development</span>
            </div>

            <div className="hexxa-service-eyebrow">
              <span className="hexxa-service-eyebrow-dot"></span>
              Custom Software Development
            </div>

            <h1>
              Custom software built around
              <br />
              <span>your business.</span>
            </h1>

            <p className="hexxa-service-hero-text">
              HexxaTech Softwares develops custom software solutions that
              match your actual business workflows, users, processes,
              approvals, data and reporting requirements.
            </p>

            <div className="hexxa-service-buttons">

              <a
                href={WHATSAPP_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="hexxa-service-btn hexxa-service-btn-primary"
                onClick={() =>
                  trackWhatsAppClick("custom_software_hero")
                }
              >
                Discuss Your Project →
              </a>

              <Link
                to="/case-studies"
                className="hexxa-service-btn hexxa-service-btn-light"
                onClick={() =>
                  trackEventCustom("case_studies_click", {
                    location: "custom_software_hero",
                  })
                }
              >
                View Our Work →
              </Link>

            </div>

          </div>

          {/* HERO VISUAL */}

          <div className="hexxa-service-visual">

            <div className="hexxa-service-card">

              <div className="hexxa-service-card-top">

                <span className="hexxa-service-card-title">
                  Custom Business Platform
                </span>

                <span className="hexxa-service-card-status">
                  Custom Built
                </span>

              </div>

              <div className="hexxa-service-modules">

                <div className="hexxa-service-module">
                  <div className="hexxa-service-module-icon">
                    ERP
                  </div>
                  <strong>Business Operations</strong>
                  <small>Centralized workflows</small>
                </div>

                <div className="hexxa-service-module">
                  <div className="hexxa-service-module-icon">
                    CRM
                  </div>
                  <strong>Customer Management</strong>
                  <small>Leads & follow-ups</small>
                </div>

                <div className="hexxa-service-module">
                  <div className="hexxa-service-module-icon">
                    INV
                  </div>
                  <strong>Inventory</strong>
                  <small>Stock visibility</small>
                </div>

                <div className="hexxa-service-module">
                  <div className="hexxa-service-module-icon">
                    BI
                  </div>
                  <strong>Reports</strong>
                  <small>Business insights</small>
                </div>

              </div>

            </div>

          </div>

        </div>
      </section>

      {/* =====================================================
          INTRODUCTION
      ===================================================== */}

      <section className="hexxa-service-section">

        <div className="hexxa-service-container hexxa-service-intro-grid">

          <div className="hexxa-service-intro-text">

            <span className="hexxa-service-label">
              Custom Software Solutions
            </span>

            <h2 className="hexxa-service-title">
              Software should adapt to your business, not the other way around.
            </h2>

            <p>
              Every business has its own processes. The way a company
              handles quotations, approvals, inventory, customers,
              production, billing, reporting or field operations may be
              completely different from another organization.
            </p>

            <p>
              Custom software development allows those processes to be
              converted into a digital system designed specifically around
              the organization.
            </p>

            <p>
              At HexxaTech Softwares, we first understand how your business
              works and then design the technology around those requirements.
              The result is software that is practical for your teams,
              centralized for management and flexible enough to evolve with
              the business.
            </p>

          </div>

          <div className="hexxa-service-intro-box">

            <h3>
              What custom software can help you achieve
            </h3>

            <div className="hexxa-intro-list">

              {[
                "Automate repetitive business processes",
                "Centralize business information",
                "Reduce dependency on spreadsheets",
                "Improve operational visibility",
                "Implement custom approval workflows",
                "Create business-specific reports",
                "Connect different systems and applications",
                "Build scalable digital infrastructure",
              ].map((item) => (
                <div className="hexxa-intro-item" key={item}>

                  <span className="hexxa-intro-check">
                    ✓
                  </span>

                  {item}

                </div>
              ))}

            </div>

          </div>

        </div>

      </section>

      {/* =====================================================
          FEATURES
      ===================================================== */}

      <section className="hexxa-service-section hexxa-service-section-soft">

        <div className="hexxa-service-container">

          <div className="hexxa-service-header-center">

            <span className="hexxa-service-label">
              What We Develop
            </span>

            <h2 className="hexxa-service-title">
              Custom software designed for real business requirements.
            </h2>

            <p className="hexxa-service-description">
              From internal business applications to complex multi-module
              platforms, we build software around the processes your
              organization actually uses.
            </p>

          </div>

          <div className="hexxa-feature-grid">

            {features.map((feature) => (

              <article
                className="hexxa-feature"
                key={feature.title}
                onClick={() =>
                  trackEventCustom("custom_software_feature_interest", {
                    feature: feature.title,
                  })
                }
              >

                <div className="hexxa-feature-icon">
                  {feature.icon}
                </div>

                <h3>
                  {feature.title}
                </h3>

                <p>
                  {feature.text}
                </p>

              </article>

            ))}

          </div>

        </div>

      </section>

      {/* =====================================================
          PROBLEMS
      ===================================================== */}

      <section className="hexxa-service-section">

        <div className="hexxa-service-container">

          <span className="hexxa-service-label">
            Business Challenges
          </span>

          <h2 className="hexxa-service-title">
            Problems custom software can solve.
          </h2>

          <p className="hexxa-service-description">
            Software development should solve operational problems rather
            than simply add another application to your technology stack.
          </p>

          <div className="hexxa-problem-grid">

            {problems.map((problem) => (

              <article
                className="hexxa-problem"
                key={problem.number}
              >

                <div className="hexxa-problem-number">
                  {problem.number}
                </div>

                <h3>
                  {problem.title}
                </h3>

                <p>
                  {problem.text}
                </p>

              </article>

            ))}

          </div>

        </div>

      </section>

      {/* =====================================================
          DEVELOPMENT PROCESS
      ===================================================== */}

      <section className="hexxa-service-section hexxa-service-section-soft">

        <div className="hexxa-service-container">

          <div className="hexxa-process-grid">

            <div>

              <span className="hexxa-service-label">
                Our Development Process
              </span>

              <h2 className="hexxa-service-title">
                From business requirement to working software.
              </h2>

              <p className="hexxa-service-description">
                Our development process focuses on understanding the
                business first and building the technology around the
                requirements.
              </p>

              <div className="hexxa-process-list">

                {processSteps.map((step) => (

                  <div
                    className="hexxa-process-item"
                    key={step.number}
                  >

                    <div className="hexxa-process-number">
                      {step.number}
                    </div>

                    <h3>
                      {step.title}
                    </h3>

                    <p>
                      {step.text}
                    </p>

                  </div>

                ))}

              </div>

            </div>

            <div className="hexxa-process-visual">

              <span className="hexxa-service-label">
                Software Workflow
              </span>

              <h3
                style={{
                  margin: "12px 0 0",
                  fontSize: "23px",
                }}
              >
                Technology follows the business process.
              </h3>

              <div className="hexxa-process-flow">

                {[
                  "Business Requirements",
                  "Functional Specification",
                  "System Architecture",
                  "Development",
                  "Testing & Validation",
                  "Deployment & Support",
                ].map((item, index) => (

                  <div
                    className="hexxa-process-flow-row"
                    key={item}
                  >

                    <div className="hexxa-process-flow-icon">
                      {String(index + 1).padStart(2, "0")}
                    </div>

                    <strong>
                      {item}
                    </strong>

                  </div>

                ))}

              </div>

              <p
                style={{
                  margin: "22px 0 0",
                  color: "#64748b",
                  fontSize: "13px",
                  lineHeight: 1.7,
                }}
              >
                The goal is not simply to develop software. The goal is to
                create a system that fits naturally into the way your
                organization operates.
              </p>

            </div>

          </div>

        </div>

      </section>

      {/* =====================================================
          USE CASES
      ===================================================== */}

      <section className="hexxa-service-section">

        <div className="hexxa-service-container">

          <span className="hexxa-service-label">
            Solutions We Build
          </span>

          <h2 className="hexxa-service-title">
            Custom applications for different business needs.
          </h2>

          <p className="hexxa-service-description">
            Custom software development can be applied to almost any
            business process where existing tools do not adequately match
            operational requirements.
          </p>

          <div className="hexxa-usecase-grid">

            {useCases.map((item) => (

              <span
                className="hexxa-usecase"
                key={item}
              >
                {item}
              </span>

            ))}

          </div>

        </div>

      </section>

      {/* =====================================================
          CAPABILITIES
      ===================================================== */}

      <section className="hexxa-service-section hexxa-service-section-soft">

        <div className="hexxa-service-container">

          <span className="hexxa-service-label">
            Technology Capabilities
          </span>

          <h2 className="hexxa-service-title">
            Built for practical business outcomes.
          </h2>

          <p className="hexxa-service-description">
            Our custom applications can combine business logic, databases,
            APIs, dashboards, automation, integrations and mobile
            experiences into one connected solution.
          </p>

          <div className="hexxa-capability-grid">

            {capabilities.map((item) => (

              <div
                className="hexxa-capability"
                key={item}
              >
                {item}
              </div>

            ))}

          </div>

        </div>

      </section>

      {/* =====================================================
          WHY HEXXATECH
      ===================================================== */}

      <section className="hexxa-service-section">

        <div className="hexxa-service-container">

          <div className="hexxa-service-header-center">

            <span className="hexxa-service-label">
              Why HexxaTech
            </span>

            <h2 className="hexxa-service-title">
              We build around the business, not around a template.
            </h2>

            <p className="hexxa-service-description">
              Our approach combines business understanding with software
              engineering to create solutions that are useful beyond the
              initial development phase.
            </p>

          </div>

          <div className="hexxa-why-grid">

            <div className="hexxa-why">
              <strong>
                Business-first approach
              </strong>

              <span>
                We understand the process before deciding how the software
                should work.
              </span>
            </div>

            <div className="hexxa-why">
              <strong>
                Custom workflows
              </strong>

              <span>
                Modules, permissions, approvals and processes can be
                designed around your requirements.
              </span>
            </div>

            <div className="hexxa-why">
              <strong>
                Scalable systems
              </strong>

              <span>
                Applications can be structured to support future modules,
                users and business growth.
              </span>
            </div>

            <div className="hexxa-why">
              <strong>
                Long-term support
              </strong>

              <span>
                We can continue supporting, maintaining and improving the
                software after deployment.
              </span>
            </div>

          </div>

        </div>

      </section>

      {/* =====================================================
          RELATED CASE STUDIES
      ===================================================== */}

      <section className="hexxa-service-section hexxa-service-section-soft">

        <div className="hexxa-service-container">

          <span className="hexxa-service-label">
            Our Work
          </span>

          <h2 className="hexxa-service-title">
            See how custom software works in real businesses.
          </h2>

          <p className="hexxa-service-description">
            Explore selected projects where software was developed around
            specific business workflows and operational requirements.
          </p>

          <div
            className="hexxa-related-box"
            style={{ marginTop: "35px" }}
          >

            <div>

              <h3>
                CarTattva — Automotive Business Platform
              </h3>

              <p>
                A custom vehicle inspection and business management platform
                covering customers, dealers, leads, appointments, evaluator
                operations, inspections and automated reporting.
              </p>

            </div>

            <Link
              to="/case-studies/cartattva"
              className="hexxa-service-btn hexxa-service-btn-primary"
              style={{ whiteSpace: "nowrap" }}
              onClick={() =>
                trackEventCustom("case_study_click", {
                  case_study: "CarTattva",
                  location: "custom_software_service_page",
                })
              }
            >
              View Case Study →
            </Link>

          </div>

          <div
            className="hexxa-related-box"
            style={{ marginTop: "15px" }}
          >

            <div>

              <h3>
                Swasthya — Healthcare Management Software
              </h3>

              <p>
                A healthcare management solution designed around patient
                records, appointments, treatment information, billing and
                day-to-day clinic operations.
              </p>

            </div>

            <Link
              to="/case-studies/swasthya"
              className="hexxa-service-btn hexxa-service-btn-primary"
              style={{ whiteSpace: "nowrap" }}
              onClick={() =>
                trackEventCustom("case_study_click", {
                  case_study: "Swasthya",
                  location: "custom_software_service_page",
                })
              }
            >
              View Case Study →
            </Link>

          </div>

          <div
            style={{
              textAlign: "center",
              marginTop: "30px",
            }}
          >

            <Link
              to="/case-studies"
              className="hexxa-service-btn hexxa-service-btn-primary"
              onClick={() =>
                trackEventCustom("case_studies_all_click", {
                  location: "custom_software_service_page",
                })
              }
            >
              Explore All Case Studies →
            </Link>

          </div>

        </div>

      </section>

      {/* =====================================================
          INTERNAL SERVICE LINKS
      ===================================================== */}

      <section className="hexxa-service-section">

        <div className="hexxa-service-container">

          <div className="hexxa-service-header-center">

            <span className="hexxa-service-label">
              Related Services
            </span>

            <h2 className="hexxa-service-title">
              Explore more software development services.
            </h2>

          </div>

          <div className="hexxa-usecase-grid">

            <Link
              to="/services/erp-business-management"
              className="hexxa-usecase"
              style={{ textDecoration: "none" }}
            >
              ERP & Business Management →
            </Link>

            <Link
              to="/services/crm-lead-management"
              className="hexxa-usecase"
              style={{ textDecoration: "none" }}
            >
              CRM & Lead Management →
            </Link>

            <Link
              to="/services/healthcare-software"
              className="hexxa-usecase"
              style={{ textDecoration: "none" }}
            >
              Healthcare Software →
            </Link>

            <Link
              to="/services/inventory-management"
              className="hexxa-usecase"
              style={{ textDecoration: "none" }}
            >
              Inventory Management →
            </Link>

            <Link
              to="/services/industrial-process-software"
              className="hexxa-usecase"
              style={{ textDecoration: "none" }}
            >
              Industrial Process Software →
            </Link>

            <Link
              to="/services/license-management"
              className="hexxa-usecase"
              style={{ textDecoration: "none" }}
            >
              License Management →
            </Link>

            <Link
              to="/services/mobile-app-development"
              className="hexxa-usecase"
              style={{ textDecoration: "none" }}
            >
              Mobile App Development →
            </Link>

          </div>

        </div>

      </section>

      {/* =====================================================
          SERVICE NAVIGATION
      ===================================================== */}

      <section
        style={{
          padding: "0 0 70px",
          background: "#ffffff",
        }}
      >
        <div
          style={{
            width: "min(100% - 32px, 1100px)",
            margin: "0 auto",
            maxWidth: "1100px",
          }}
        >
          <span className="hexxa-service-label">
            Explore Our Services
          </span>

          <h2 className="hexxa-service-title" style={{ marginTop: "12px" }}>
            Explore our other software development services.
          </h2>

          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
              gap: "12px",
              marginTop: "22px",
            }}
          >
            {serviceNavigation.map((service) => {
              const isCurrent = service.to === PAGE_URL;

              return (
                <Link
                  key={service.to}
                  to={service.to}
                  style={{
                    display: "block",
                    padding: "16px 18px",
                    borderRadius: "12px",
                    textDecoration: "none",
                    border: isCurrent ? "1px solid #2563eb" : "1px solid #e5e7eb",
                    background: isCurrent ? "#eff6ff" : "#f8fafc",
                    color: isCurrent ? "#1d4ed8" : "#334155",
                    fontWeight: isCurrent ? 700 : 600,
                    transition: "all 0.2s ease",
                  }}
                >
                  {service.title}
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      <section
        style={{
          padding: "0 0 70px",
          background: "#ffffff",
        }}
      >
        <div
          style={{
            width: "min(100% - 32px, 1100px)",
            margin: "0 auto",
            maxWidth: "1100px",
          }}
        >
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              gap: "12px",
              flexWrap: "wrap",
              borderTop: "1px solid #e5e7eb",
              paddingTop: "20px",
            }}
          >
            <Link
              to={previousService.to}
              style={{
                color: "#2563eb",
                textDecoration: "none",
                fontWeight: 600,
              }}
            >
              ← Previous Service: {previousService.title}
            </Link>

            <Link
              to={nextService.to}
              style={{
                color: "#2563eb",
                textDecoration: "none",
                fontWeight: 600,
              }}
            >
              Next Service: {nextService.title} →
            </Link>
          </div>
        </div>
      </section>

      {/* =====================================================
          FAQ
      ===================================================== */}

      <section className="hexxa-service-section hexxa-service-section-soft">

        <div className="hexxa-service-container">

          <div className="hexxa-service-header-center">

            <span className="hexxa-service-label">
              Frequently Asked Questions
            </span>

            <h2 className="hexxa-service-title">
              Custom software development FAQs.
            </h2>

          </div>

          <div className="hexxa-faq-list">

            {faqs.map((faq) => (

              <article
                className="hexxa-faq"
                key={faq.question}
              >

                <h3>
                  {faq.question}
                </h3>

                <p>
                  {faq.answer}
                </p>

              </article>

            ))}

          </div>

        </div>

      </section>

      {/* =====================================================
          CTA
      ===================================================== */}

      <section className="hexxa-service-cta">

        <div className="hexxa-service-container">

          <div className="hexxa-service-cta-box">

            <span className="hexxa-service-cta-label">
              Start Your Software Project
            </span>

            <h2>
              Have a business process that needs better software?
            </h2>

            <p>
              Tell us about your current workflow, business challenges,
              users and goals. We can help turn your requirements into a
              practical custom software solution.
            </p>

            <a
              href={WHATSAPP_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="hexxa-service-btn hexxa-service-btn-primary"
              onClick={() =>
                trackWhatsAppClick("custom_software_bottom_cta")
              }
            >
              Discuss Your Project →
            </a>

          </div>

        </div>

      </section>

    </div>
  );
}